// Shared helpers and constants used by CompanyDetailPage and its sub-components.

import {
  mergeSubsidiariesWithLei,
  SWISS_CANTON_NAMES,
} from '@/lib/company-utils';
import type {
  CompanyBasicResponse,
  CompanyRegisterResponse,
  CompanyFinancialsResponse,
  CompanyHistory,
} from '@/lib/backend-api';
import type {
  CompanyProfileHeroData,
  CompanyProfileStatistics,
} from './CompanyProfileSection';
import type { createFormatters } from './currencyFormatters';

/**
 * i18n key for the ownership sentence woven into the company description
 * (AboutSection) and the SEO meta description. Only the family and
 * succession verdicts are voiced — founder-led stays a badge in the
 * FounderProfileCard. Verdicts are tri-state; anything but an explicit
 * `true` yields no sentence.
 */
export function ownershipSentenceKey(
  profile:
    | { family_managed?: boolean | null; succession_risk?: boolean | null }
    | null
    | undefined,
): string | null {
  const family = profile?.family_managed === true;
  const succession = profile?.succession_risk === true;
  if (family && succession)
    return 'companyDetail.ownership.familySuccessionSentence';
  if (family) return 'companyDetail.ownership.familySentence';
  if (succession) return 'companyDetail.ownership.successionSentence';
  return null;
}

/**
 * i18n key for the size-class sentence woven into the same About prose.
 *
 * Only voiced when the company published no turnover — where a figure exists
 * the class is redundant, and saying it would imply the class is why the
 * figure looks the way it does. `kleinst` and `klein` are voiced because they
 * carry a disclosure consequence a reader can act on (no P&L is filed);
 * `mittelgross` is not, because the § 276 Rohergebnis case is already visible
 * in the table as a real figure.
 *
 * Never voiced for a missing class: absence means "we could not say", never
 * "large".
 */
export function sizeClassSentenceKey(
  metric:
    | { total_revenue?: number | null; size_class?: string | null }
    | null
    | undefined,
): string | null {
  if (!metric || metric.total_revenue != null) return null;
  if (metric.size_class === 'kleinst')
    return 'companyDetail.sizeClass.sentence.kleinst';
  if (metric.size_class === 'klein')
    return 'companyDetail.sizeClass.sentence.klein';
  return null;
}

/** Formats a monetary value with the given formatter; returns "—" for empty/zero. */
export function formatStatCurrency(
  value: number | null | undefined,
  fmt: { format: (v: number) => string },
) {
  if (value === null || value === undefined || value === 0) return '—';
  return fmt.format(value);
}

/** Formats a numeric value with the given formatter; returns "—" for empty/zero. */
export function formatStatNumber(
  value: number | null | undefined,
  fmt: { format: (v: number) => string },
) {
  if (value === null || value === undefined || value === 0) return '—';
  return fmt.format(value);
}

/** Computes age in years from a date-of-birth string; null if unparseable or future. */
export function calculateAge(dateOfBirth?: string | null): number | null {
  if (!dateOfBirth) return null;
  const birth = new Date(dateOfBirth);
  if (isNaN(birth.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  if (
    now.getMonth() < birth.getMonth() ||
    (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())
  )
    age--;
  return age > 0 ? age : null;
}

/** Renders a prefixed age label like "Age 42"; null if age is missing. */
export function formatAge(age?: number | null, ageLabel = 'Age') {
  if (age === null || age === undefined) return null;
  return `${ageLabel} ${age}`;
}

/**
 * Normalize a date string to ISO format (YYYY-MM-DD).
 * Handles both ISO "2009-05-12" and German "29.05.2009" formats.
 * Returns null for unparseable values.
 */
export function normalizeToIsoDate(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
  const german = trimmed.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (german) {
    const [, d, m, y] = german;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return null;
}

/** Convert an ISO date (YYYY-MM-DD) to German display format (DD.MM.YYYY). */
export function isoToGerman(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

/** Returns true when the date string is today (i.e. freshly downloaded). */
export function isDateToday(raw: string | undefined | null): boolean {
  if (!raw) return false;
  const fileDate = new Date(raw);
  const today = new Date();
  return (
    fileDate.getFullYear() === today.getFullYear() &&
    fileDate.getMonth() === today.getMonth() &&
    fileDate.getDate() === today.getDate()
  );
}

export interface CompanyFactsInput {
  name?: string | null;
  city?: string | null;
  registerCourt?: string | null;
  registerType?: string | null;
  registerId?: string | null;
  foundingYear?: string | null;
  latestRevenue?: number | null;
  latestRevenueYear?: number | null;
  latestEmployees?: number | null;
}

/** Minimal i18next translate signature (interpolation values → string). */
type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/** Remove a duplicated German court prefix before localized presentation. */
export function normalizeRegisterCourt(
  registerCourt: string | null | undefined,
): string | null {
  const court = (registerCourt || '').replace(/^Amtsgericht\s+/i, '').trim();
  return court || null;
}

/** Format the separately stored register type and number for display. */
export function formatRegisterNumber(
  registerType: string | null | undefined,
  registerId: string | null | undefined,
): string | null {
  const id = registerId?.trim();
  if (!id) return null;
  return registerType === 'CH-HR'
    ? id
    : registerType
      ? `${registerType} ${id}`
      : id;
}

/**
 * Build a short, deterministic factual description of a company from its
 * register and headline financial data. Used as an always-present lead in the
 * About section so even content-thin pages carry unique, crawlable prose.
 * Returns null when there isn't enough to say anything meaningful (no name).
 *
 * Phrasing comes entirely from i18n (`companyDetail.facts.*`) so every locale
 * reads naturally; sentences are assembled from whole-sentence fragments to
 * avoid cross-language word-order/grammar issues.
 */
export function buildCompanyFactsSentence(
  input: CompanyFactsInput,
  t: TranslateFn,
  fmt: {
    fullCurrency: { format: (v: number) => string };
    fullNumber: { format: (v: number) => string };
  },
): string | null {
  const name = input.name?.trim();
  if (!name) return null;

  // Swiss rows store the canton code in registerCourt ("VD") — that is not
  // an Amtsgericht, so the register sentence names the canton instead.
  const isSwiss = input.registerType === 'CH-HR';
  const court = isSwiss
    ? SWISS_CANTON_NAMES[(input.registerCourt || '').toUpperCase()] || ''
    : normalizeRegisterCourt(input.registerCourt) || '';
  const register =
    input.registerType && input.registerId
      ? formatRegisterNumber(input.registerType, input.registerId)
      : null;
  const city = input.city?.trim() || null;

  const sentences: string[] = [];

  sentences.push(
    city
      ? t('companyDetail.facts.intro', { name, city })
      : t('companyDetail.facts.introNoCity', { name }),
  );

  if (court && register)
    sentences.push(
      t(
        isSwiss
          ? 'companyDetail.facts.registerSwiss'
          : 'companyDetail.facts.register',
        { court, register },
      ),
    );
  else if (register)
    sentences.push(t('companyDetail.facts.registerNoCourt', { register }));

  if (input.foundingYear)
    sentences.push(
      t('companyDetail.facts.founded', { year: input.foundingYear }),
    );

  if (input.latestRevenue != null && input.latestRevenue !== 0) {
    const revenue = fmt.fullCurrency.format(input.latestRevenue);
    const year = input.latestRevenueYear;
    const employees =
      input.latestEmployees != null && input.latestEmployees !== 0
        ? fmt.fullNumber.format(input.latestEmployees)
        : null;
    if (year && employees)
      sentences.push(
        t('companyDetail.facts.financialsEmployees', {
          year,
          revenue,
          employees,
        }),
      );
    else if (year)
      sentences.push(t('companyDetail.facts.financials', { year, revenue }));
    else if (employees)
      sentences.push(
        t('companyDetail.facts.financialsNoYearEmployees', {
          revenue,
          employees,
        }),
      );
    else sentences.push(t('companyDetail.facts.financialsNoYear', { revenue }));
  }

  return sentences.join(' ');
}

/** i18next language code → Intl locale tag used for date/number formatting. */
export const LOCALE_MAP: Record<string, string> = {
  de: 'de-DE',
  en: 'en-GB',
  es: 'es-ES',
  ar: 'ar-SA',
  ch: 'zh-CN',
};

/** Format an ISO timestamp as a locale-aware day-level date; null if unparseable. */
export function formatCapturedDate(
  captured: string | null | undefined,
  language: string,
): string | null {
  if (!captured) return null;
  const date = new Date(captured);
  if (isNaN(date.getTime())) return null;
  const locale = LOCALE_MAP[language] ?? 'de-DE';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(date);
}

/** Regexes for classifying a Gesellschafter row as person vs. company. */
export const PERSON_TYPE_REGEX =
  /(person|individual|natural|nat(ü|u)rlich|privat)/i;
export const COMPANY_TYPE_REGEX =
  /(company|organization|firma|unternehmen|juristisch|gesellschaft|corporate)/i;

/** File types the financial-docs card will expose for download. */
export const DOWNLOADABLE_FILE_TYPES = [
  'Bilanz',
  'HandelsRegisterAD',
  'HandelsRegisterCD',
  'HandelsRegisterDKGesellschafter',
];

/** Register-docs file types that can be refreshed from Handelsregister in real-time. */
export const REFRESHABLE_FILE_TYPES = new Set([
  'HandelsRegisterAD',
  'HandelsRegisterCD',
  'HandelsRegisterDKGesellschafter',
  'HandelsRegisterDKSatzung',
  'HandelsRegisterDKMusterProtokol',
  'HandelsRegisterDKAnmeldung',
]);

/** Offer founding documents by legal form; the model protocol requires a post-MoMiG filing date. */
export function expectedRegisterDocTypes({
  hasGesellschafterliste,
  rechtsformCode,
  gruendungsdokument,
  ersteEintragungDatum,
}: {
  hasGesellschafterliste: boolean;
  rechtsformCode?: number | null;
  gruendungsdokument?: string | null;
  ersteEintragungDatum?: string | null;
}): string[] {
  const code = rechtsformCode == null ? '' : String(rechtsformCode);
  const hasConstitutionalDocument =
    !!gruendungsdokument?.trim() ||
    ['21', '22', '25', '231'].some((prefix) => code.startsWith(prefix));
  const firstEntryDate = normalizeToIsoDate(ersteEintragungDatum);

  const types = ['HandelsRegisterAD', 'HandelsRegisterCD'];
  if (hasGesellschafterliste) types.push('HandelsRegisterDKGesellschafter');
  if (hasConstitutionalDocument) types.push('HandelsRegisterDKSatzung');
  if (
    hasGesellschafterliste &&
    (!ersteEintragungDatum?.trim() ||
      (firstEntryDate !== null && firstEntryDate >= '2008-11-01'))
  )
    types.push('HandelsRegisterDKMusterProtokol');
  types.push('HandelsRegisterDKAnmeldung');
  return types;
}

/** DK types confirmed absent from both stored files and a completely walked catalog. */
export function confirmedAbsentRegisterDocTypes(
  registerDocumentsComplete: readonly string[],
  storedFileTypes: ReadonlySet<string>,
  registerDocuments: readonly { file_type: string }[],
): Set<string> {
  const catalogTypes = new Set(registerDocuments.map((doc) => doc.file_type));
  return new Set(
    registerDocumentsComplete.filter(
      (type) =>
        type.startsWith('HandelsRegisterDK') &&
        !storedFileTypes.has(type) &&
        !catalogTypes.has(type),
    ),
  );
}

/** Use the filed name when known; GmbH-family forms otherwise file a Gesellschaftsvertrag. */
export function constitutionalDocKind(
  gruendungsdokument?: string | null,
  rechtsformCode?: number | null,
): 'gesellschaftsvertrag' | 'satzung' | 'statut' {
  const name = gruendungsdokument?.trim().toLowerCase();
  if (
    name === 'gesellschaftsvertrag' ||
    name === 'satzung' ||
    name === 'statut'
  )
    return name;
  return rechtsformCode != null && String(rechtsformCode).startsWith('221')
    ? 'gesellschaftsvertrag'
    : 'satzung';
}

export const REFRESH_FILE_TYPE_MAP: Record<string, 'AD' | 'CD' | 'DK'> = {
  HandelsRegisterAD: 'AD',
  HandelsRegisterCD: 'CD',
  HandelsRegisterDKGesellschafter: 'DK',
};

/** CD is auto-updated by the /register endpoint. */
export const REGISTER_AUTO_UPDATED_TYPES = new Set(['HandelsRegisterCD']);

/** DK is auto-updated by the /gesellschafter endpoint. */
export const GESELLSCHAFTER_AUTO_UPDATED_TYPES = new Set([
  'HandelsRegisterDKGesellschafter',
  'HandelsRegisterDKSatzung',
  'HandelsRegisterDKMusterProtokol',
  'HandelsRegisterDKAnmeldung',
]);

/** Apply owner overrides and the basic endpoint fallback identically on both surfaces. */
export function getEffectiveCompanyRegisterData(
  basicData: CompanyBasicResponse | null,
  registerData: CompanyRegisterResponse | null,
): CompanyRegisterResponse | null {
  const overrides = basicData?.overrides;
  const base =
    registerData ??
    (basicData
      ? {
          eu_id: basicData.eu_id,
          gegenstand: basicData.gegenstand,
          rechtsform_code: basicData.rechtsform_code,
          street: basicData.street,
          house_no: basicData.house_no,
          postal_code: basicData.postal_code,
          city: basicData.city,
          sitz: basicData.sitz,
          participants: basicData.participants ?? [],
          entries: basicData.entries ?? [],
          location: basicData.location,
          nace_codes: basicData.nace_codes ?? [],
          register_files: basicData.register_files ?? [],
          register_documents: basicData.register_documents ?? [],
          register_documents_complete: basicData.register_documents_complete ?? [],
          gruendungsdokument: basicData.gruendungsdokument,
          gruendungsdokument_datum: basicData.gruendungsdokument_datum,
          erste_eintragung_datum: basicData.erste_eintragung_datum,
          has_earlier_entries: basicData.has_earlier_entries,
        }
      : null);
  if (!base) return null;

  // WARNING: Company owner overrides have HIGHEST PRIORITY — never
  // let register, backfill, or AI data take precedence over these.
  if (overrides) {
    return {
      ...base,
      street: overrides.street ?? base.street,
      house_no: overrides.house_no ?? base.house_no,
      postal_code: overrides.postal_code ?? base.postal_code,
      city: overrides.city ?? base.city,
    };
  }
  return base;
}

/** Merge LEI subsidiaries and preserve the company page's basic financial fallback. */
export function getEffectiveCompanyFinancialsData(
  basicData: CompanyBasicResponse | null,
  financialsData: CompanyFinancialsResponse | null,
  euId: string,
): CompanyFinancialsResponse | null {
  const selfEuId = basicData?.eu_id ?? euId;
  const leiChildren = basicData?.lei_children ?? [];

  if (financialsData) {
    return {
      ...financialsData,
      subsidiaries: mergeSubsidiariesWithLei(
        financialsData.subsidiaries,
        leiChildren,
        selfEuId,
      ),
    };
  }
  if (!basicData) return null;
  const hasFinancials =
    (basicData.metrics?.length ?? 0) > 0 ||
    (basicData.guv_data?.length ?? 0) > 0;
  const hasAi =
    !!basicData.ai_summary ||
    !!basicData.ai_outlook ||
    (basicData.risk_keywords?.length ?? 0) > 0 ||
    (basicData.opportunity_keywords?.length ?? 0) > 0 ||
    (basicData.employee_data?.length ?? 0) > 0 ||
    (basicData.products?.length ?? 0) > 0;
  const hasSubsidiaries =
    (basicData.subsidiaries?.length ?? 0) > 0 ||
    leiChildren.some((c) => c.relationship === 'IS_DIRECTLY_CONSOLIDATED_BY');
  const hasFinancialFiles = (basicData.financial_files?.length ?? 0) > 0;
  if (!hasFinancials && !hasAi && !hasSubsidiaries && !hasFinancialFiles)
    return null;
  return {
    eu_id: basicData.eu_id,
    metrics: basicData.metrics ?? [],
    aktiva_data: [],
    passiva_data: [],
    guv_data: basicData.guv_data ?? [],
    ai_guv_data: [],
    subsidiaries: mergeSubsidiariesWithLei(
      basicData.subsidiaries,
      leiChildren,
      selfEuId,
    ),
    financial_files: basicData.financial_files ?? [],
    validation_logs: [],
    ai_summary: basicData.ai_summary,
    ai_outlook: basicData.ai_outlook,
    ai_summary_en: basicData.ai_summary_en,
    ai_outlook_en: basicData.ai_outlook_en,
    risk_keywords: basicData.risk_keywords ?? [],
    opportunity_keywords: basicData.opportunity_keywords ?? [],
    employee_data: basicData.employee_data ?? [],
    products: basicData.products ?? [],
    business_branches: basicData.business_branches ?? [],
    token_limit_reached: false,
  };
}

/** Shared profile identity and foundation-date derivation. */
export function buildCompanyProfileHeroData(
  basicData: CompanyBasicResponse | null,
  effectiveRegisterData: CompanyRegisterResponse | null,
  companyHistory: CompanyHistory | null,
  euId: string,
): CompanyProfileHeroData {
  if (!basicData) {
    return {
      name: 'Company',
      initial: euId.charAt(0).toUpperCase() || 'C',
      registerType: null,
      address: null,
      euId,
      gruendungsDatum: null,
      gruendungsYear: null,
      gruendungsdokumentDatum: null,
      ersteEintragungDatum: null,
      gruendungsdokument: null,
      isDeleted: false,
    };
  }
  const registerAddress =
    effectiveRegisterData?.street || effectiveRegisterData?.city
      ? [
          [effectiveRegisterData.street, effectiveRegisterData.house_no]
            .filter(Boolean)
            .join(' '),
          [effectiveRegisterData.postal_code, effectiveRegisterData.city]
            .filter(Boolean)
            .join(' '),
        ]
          .filter(Boolean)
          .join(', ')
      : null;
  const regData = effectiveRegisterData;
  const ersteEintragungData = companyHistory?.erste_eintragung?.data;
  // Normalize all dates to ISO format (handles both "2009-05-12" and "29.05.2009")
  const gruendungsdokumentDatum = normalizeToIsoDate(
    regData?.gruendungsdokument_datum ??
      basicData.gruendungsdokument_datum ??
      ersteEintragungData?.gruendungsdokument_datum,
  );
  const ersteEintragungDatum = normalizeToIsoDate(
    regData?.erste_eintragung_datum ??
      basicData.erste_eintragung_datum ??
      ersteEintragungData?.erste_eintragung_datum,
  );
  const gruendungsdokument =
    regData?.gruendungsdokument ??
    basicData.gruendungsdokument ??
    ersteEintragungData?.gruendungsdokument ??
    null;
  // Gründungsdatum = earliest of the two dates (ISO strings compare correctly)
  const candidates = [gruendungsdokumentDatum, ersteEintragungDatum].filter(
    Boolean,
  ) as string[];
  const gruendungsDatum =
    candidates.length > 0 ? candidates.reduce((a, b) => (a < b ? a : b)) : null;
  const gruendungsYear = gruendungsDatum?.slice(0, 4) ?? null;
  const baseName = basicData.display_name;
  const displayName = baseName
    ? baseName.replace(/\s+i\.\s?L\.?$/i, '').trim()
    : baseName;
  return {
    name: displayName,
    initial: basicData.display_name?.charAt(0) ?? basicData.eu_id.charAt(0),
    registerType: basicData.register_type ?? null,
    address:
      registerAddress ??
      effectiveRegisterData?.sitz ??
      basicData.address_line ??
      basicData.register_city ??
      null,
    euId: basicData.eu_id,
    gruendungsDatum,
    gruendungsYear,
    gruendungsdokumentDatum,
    ersteEintragungDatum,
    gruendungsdokument,
    isDeleted: basicData.is_deleted ?? false,
  };
}

/** Shared four headline statistics, including formatting and translated labels. */
export function buildCompanyProfileStatistics(
  effectiveFinancialsData: CompanyFinancialsResponse | null,
  t: TranslateFn,
  fmt: ReturnType<typeof createFormatters>,
): CompanyProfileStatistics {
  const latestMetric = effectiveFinancialsData?.metrics?.[0];
  const latestEmployee =
    effectiveFinancialsData?.employee_data?.find((e) => e.total != null) ??
    effectiveFinancialsData?.employee_data?.[0];

  return [
    {
      number: formatStatCurrency(latestMetric?.profit, fmt.compactCurrency),
      label: t('companyDetail.trends.profit'),
    },
    {
      number: formatStatCurrency(
        latestMetric?.total_revenue,
        fmt.compactCurrency,
      ),
      label: t('companyDetail.trends.revenue'),
    },
    {
      number: formatStatNumber(latestEmployee?.total, fmt.compactNumber),
      label: t('companyDetail.trends.employees'),
    },
    {
      number: formatStatCurrency(
        latestMetric?.total_balance_sum ?? latestMetric?.passiva,
        fmt.compactCurrency,
      ),
      label: t('companyDetail.trends.balance'),
    },
  ];
}
